import { useInviteMutation } from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/base/dialog';
import { FormField } from '@/components/base/form/form-field';
import { Input } from '@/components/base/form/input';
import { PageHeading } from '@/components/base/page-heading';
import { cn } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from 'notistack';
import { type FormEvent, type PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface EmailCodeComponentProps {
  onOpenChange: (value: boolean) => void;
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
}

export function InviteModal({
  onOpenChange,
  isOpen,
  children,
  className,
}: PropsWithChildren<EmailCodeComponentProps>): JSX.Element {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [invite] = useInviteMutation();

  const InviteScheme = z.object({
    email: z.string().email(),
  });

  type Invite = z.infer<typeof InviteScheme>;
  const methods = useForm<Invite>({
    resolver: zodResolver(InviteScheme),
    mode: 'onChange',
  });
  const { handleSubmit } = methods;

  async function inviteUser(data: z.infer<typeof InviteScheme>): Promise<void> {
    const { email } = data;
    try {
      await invite({
        variables: {
          email,
        },
      });
      onOpenChange(false);
      enqueueSnackbar({
        message: t('affiliateDashboard.invite.invited'),
        variant: 'success',
      });
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message: e.message,
          variant: 'error',
        });
      }
    }
  }

  function handleClickSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      await handleSubmit(inviteUser)();
    })();
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value) => {
        onOpenChange(value);
      }}
    >
      <DialogTrigger
        asChild
        onClick={() => {
          onOpenChange(true);
        }}
      >
        <span
          className={cn(
            'flex h-full w-fit cursor-pointer items-end justify-center text-sm font-normal',
            className,
          )}
        >
          {children}
        </span>
      </DialogTrigger>
      <DialogContent className="max-w-3xl gap-6 md:pb-0">
        <div className="mx-auto flex w-full flex-col">
          <div className="mb-2 text-start">
            <PageHeading subheading={t('affiliateDashboard.invite.subheading')}>
              {t('affiliateDashboard.invite.title')}
            </PageHeading>
          </div>
          <div>
            <FormProvider {...methods}>
              <form
                onSubmit={(e) => {
                  handleClickSave(e);
                }}
              >
                <div className="flex h-full flex-col items-end justify-between gap-5 py-5 sm:flex-row">
                  <div className="flex h-full w-full">
                    <fieldset className="w-full">
                      <FormField
                        labelClassName="dark:text-white text-default-gray-950"
                        label={t('affiliateDashboard.invite.email')}
                        name="email"
                      >
                        <Input
                          data-testid="firstName"
                          variant="secondary"
                          placeholder={t(
                            'affiliateDashboard.invite.emailPlaceholder',
                          )}
                          className="w-full"
                        />
                      </FormField>
                    </fieldset>
                  </div>

                  <div className="flex w-full items-center justify-end sm:w-fit">
                    <Button type="submit">
                      {t('affiliateDashboard.invite.invite')}
                    </Button>
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
